import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Collapse,
  Tooltip,
  Fade,
  Zoom,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CodeIcon from "@mui/icons-material/Code";
import SearchIcon from "@mui/icons-material/Search";
import DataObjectIcon from "@mui/icons-material/DataObject";
import SettingsIcon from "@mui/icons-material/Settings";
import EmailIcon from "@mui/icons-material/Email";
import CalculateIcon from "@mui/icons-material/Calculate";
import { TaskStep, StepInput } from "../../types/tasks";
import { useTranslation } from "react-i18next";

const stepTypeIcons = {
  search: <SearchIcon />,
  code: <CodeIcon />,
  data: <DataObjectIcon />,
  config: <SettingsIcon />,
  email: <EmailIcon />,
  calculation: <CalculateIcon />,
  default: <PlayArrowIcon />,
};

const formatInputsToString = (inputs: StepInput[] | string): string => {
  if (typeof inputs === "string") {
    return inputs.trim();
  }
  return inputs.map(input => input.value.trim()).join(". ");
};

const getStepIcon = (title: string) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes("pesquis") || lowerTitle.includes("busc"))
    return stepTypeIcons.search;
  if (lowerTitle.includes("código") || lowerTitle.includes("program"))
    return stepTypeIcons.code;
  if (lowerTitle.includes("dados") || lowerTitle.includes("informaç"))
    return stepTypeIcons.data;
  if (lowerTitle.includes("configur") || lowerTitle.includes("ajust"))
    return stepTypeIcons.config;
  if (lowerTitle.includes("email") || lowerTitle.includes("mensagem"))
    return stepTypeIcons.email;
  if (lowerTitle.includes("cálculo") || lowerTitle.includes("calcul"))
    return stepTypeIcons.calculation;
  return stepTypeIcons.default;
};

const getEstimatedTime = (title: string) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes("pesquis") || lowerTitle.includes("busc"))
    return "~2 min";
  if (lowerTitle.includes("código") || lowerTitle.includes("program"))
    return "~5 min";
  if (lowerTitle.includes("dados") || lowerTitle.includes("informaç"))
    return "~3 min";
  if (lowerTitle.includes("configur") || lowerTitle.includes("ajust"))
    return "~2 min";
  if (lowerTitle.includes("email") || lowerTitle.includes("mensagem"))
    return "~1 min";
  if (lowerTitle.includes("cálculo") || lowerTitle.includes("calcul"))
    return "~3 min";
  return "~2 min";
};

type TaskExecutionItemProps = {
  item: TaskStep;
  index: number;
  isActive: boolean;
  isComplete?: boolean;
  onComplete: () => void;
};

const TaskExecutionItem: React.FC<TaskExecutionItemProps> = ({
  item,
  index,
  isActive,
  isComplete = false,
  onComplete,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const isCompleted = isComplete || item.isCompleted;
  const inputsString = formatInputsToString(item.inputs);
  const stepIcon = getStepIcon(inputsString);
  const estimatedTime = getEstimatedTime(inputsString);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (isActive && !isCompleted) {
      setProgress(0);
      timer = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(timer!);
            onComplete();
            return 100;
          }
          return prev + 1;
        });
      }, 300);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isActive, isCompleted, onComplete]);

  useEffect(() => {
    if (isCompleted) {
      setProgress(100);
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isCompleted]);

  useEffect(() => {
    if (isActive && (item.substeps ?? []).length > 0) {
      let stepIndex = 0;

      const substepTimer = setInterval(() => {
        stepIndex++;
        if (stepIndex < (item.substeps ?? []).length) {
        } else {
          clearInterval(substepTimer);
        }
      }, 1500);

      return () => clearInterval(substepTimer);
    }
  }, [isActive, item.substeps]);

  const toggleExpand = () => setExpanded(!expanded);

  return (
    <Fade in timeout={300} style={{ transitionDelay: `${index * 100}ms` }}>
      <Box
        ref={cardRef}
        sx={{
          mb: "0.9375em",
          position: "relative",
          transform: isActive ? "scale(1.01)" : "scale(1)",
          transition: "all 0.3s ease",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "0.25em",
            borderRadius: "0.5em 0 0 0.5em",
            backgroundColor: isCompleted
              ? "#60A5FA"
              : isActive
                ? "#93C5FD"
                : "#CBD5E1",
          }}
        />

        <Paper
          elevation={isActive ? 1 : 0}
          sx={{
            borderRadius: "0.375em",
            border: "0.0625em solid",
            borderColor: isCompleted
              ? "#DBEAFE"
              : isActive
                ? "#BFDBFE"
                : "#E2E8F0",
            backgroundColor: isCompleted
              ? "#EFF6FF"
              : isActive
                ? "#F0F9FF"
                : "#F8FAFC",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "0.9375em",
              cursor: "pointer",
            }}
            onClick={toggleExpand}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.625em",
                flex: 1,
              }}
            >
              <Zoom in timeout={500}>
                <Box
                  sx={{
                    width: "1.75em",
                    height: "1.75em",
                    borderRadius: "50%",
                    backgroundColor: isCompleted
                      ? "#DBEAFE"
                      : isActive
                        ? "#E0F2FE"
                        : "#F1F5F9",
                    color: isCompleted
                      ? "#3B82F6"
                      : isActive
                        ? "#2563EB"
                        : "#94A3B8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isCompleted ? (
                    <CheckCircleIcon fontSize="small" />
                  ) : (
                    stepIcon
                  )}
                </Box>
              </Zoom>

              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color={
                    isCompleted ? "#2563EB" : isActive ? "#1D4ED8" : "#64748B"
                  }
                >
                  {inputsString}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.625em",
                    mt: "0.125em",
                  }}
                >
                  <Chip
                    size="small"
                    label={
                      isCompleted
                        ? t("task.completed")
                        : isActive
                          ? t("task.inProgress")
                          : t("task.pending")
                    }
                    sx={{
                      height: "1.125em",
                      fontSize: "0.625rem",
                      fontWeight: 600,
                      backgroundColor: isCompleted
                        ? "#DBEAFE"
                        : isActive
                          ? "#DBEAFE"
                          : "#F1F5F9",
                      color: isCompleted
                        ? "#2563EB"
                        : isActive
                          ? "#1D4ED8"
                          : "#64748B",
                    }}
                  />
                  {!isCompleted && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.3125em",
                        color: isActive ? "#1D4ED8" : "#94A3B8",
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: "0.7rem" }} />
                      <Typography variant="caption">{estimatedTime}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{ display: "flex", alignItems: "center", color: "#60A5FA" }}
            >
              <Tooltip
                title={t("task.tooltipMoreInfo")}
                arrow
                open={showTooltip}
                onOpen={() => setShowTooltip(true)}
                onClose={() => setShowTooltip(false)}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: "1rem",
                    mr: "0.625em",
                    cursor: "pointer",
                    opacity: 0.7,
                    "&:hover": { opacity: 1 },
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setShowTooltip(!showTooltip);
                  }}
                />
              </Tooltip>
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </Box>
          </Box>

          {isActive && !isCompleted && (
            <Box
              sx={{
                position: "relative",
                height: "0.25em",
                overflow: "hidden",
                backgroundColor: "#DBEAFE",
                borderRadius: "0.125em",
                mt: "-0.0625em",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: "200%",
                  height: "100%",
                  backgroundImage: `
                    repeating-linear-gradient(
                      45deg,
                      #93C5FD 0em,
                      #93C5FD 1em,
                      transparent 1em,
                      transparent 2.5em
                    )
                  `,
                  animation: "moveStripes 2.4s linear infinite",
                }}
              />
              <style>
                {`
                  @keyframes moveStripes {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-25%); }
                  }
                `}
              </style>
            </Box>
          )}

          <Collapse in={expanded} timeout="auto">
            <Box sx={{ p: "0.9375em", pt: 0 }}>
              {item.humanSupport?.length &&
                item.humanSupport.map((support, index) =>
                  support.humanSupportMessage?.[0]?.question ? (
                    <Box
                      key={index}
                      sx={{
                        mt: "0.9375em",
                        p: "0.75em",
                        backgroundColor: "#EFF6FF",
                        borderRadius: "0.375em",
                        border: "0.0625em solid #BFDBFE",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {support.humanSupportMessage?.[0]?.question}
                      </Typography>
                    </Box>
                  ) : null,
                )}

              {typeof item.output === "string" && item.output && (
                <Box sx={{ mt: "0.9375em" }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ mb: "0.625em" }}
                  >
                    {t("task.result")}
                  </Typography>
                  <Box
                    sx={{
                      p: "0.75em",
                      backgroundColor: "#EFF6FF",
                      borderRadius: "0.375em",
                      border: "0.0625em solid #BFDBFE",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {item.output.trim()}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </Paper>
      </Box>
    </Fade>
  );
};

export default TaskExecutionItem;
